import React, { useState, useEffect } from 'react';
import { letterAPI } from '../../../services';
import { useAsyncAction } from '../../../hooks';
import { useAuth } from '../../../context/AuthContext';
import {
  Button,
  Card,
  Badge,
  LoadingSpinner,
  ErrorMessage,
  SuccessMessage,
} from '../../UI';
import { 
  FileText, 
  Download, 
  Briefcase, 
  BadgeCheck,
  AlertCircle,
  Eye,
  FolderOpen
} from 'lucide-react';

// Letter type labels
const letterTypeLabels = {
  offer: 'Offer Letter',
  appointment: 'Appointment Letter',
  confirmation: 'Confirmation Letter',
  experience: 'Experience Letter',
  relieving: 'Relieving Letter',
  promotion: 'Promotion Letter',
  increment: 'Increment Letter',
  warning: 'Warning Letter',
  termination: 'Termination Letter',
  other: 'Other',
};

const EmployeeLettersPage = () => {
  const { user } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadedLetters, setUploadedLetters] = useState([]);
  const [loadingLetters, setLoadingLetters] = useState(true);

  // Letter download hooks
  const { loading: offerLoading, execute: executeOffer } = useAsyncAction();
  const { loading: appointmentLoading, execute: executeAppointment } = useAsyncAction();
  const { loading: experienceLoading, execute: executeExperience } = useAsyncAction();

  useEffect(() => {
    fetchUploadedLetters();
  }, []);

  const fetchUploadedLetters = async () => {
    try {
      const response = await letterAPI.getMyUploadedLetters();
      setUploadedLetters(response.data || []);
    } catch (err) {
      console.error('Error fetching uploaded letters:', err);
    } finally {
      setLoadingLetters(false);
    }
  };

  const handleOfferLetter = async () => {
    try {
      await executeOffer(letterAPI.getMyOfferLetter);
      setSuccess('Offer letter downloaded successfully');
    } catch (err) {
      setError(err.message || 'Failed to download offer letter');
    }
  };

  const handleAppointmentLetter = async () => {
    try {
      await executeAppointment(letterAPI.getMyAppointmentLetter);
      setSuccess('Appointment letter downloaded successfully');
    } catch (err) {
      setError(err.message || 'Failed to download appointment letter');
    }
  };

  const handleExperienceLetter = async () => {
    try {
      await executeExperience(letterAPI.getMyExperienceLetter);
      setSuccess('Experience letter downloaded successfully');
    } catch (err) {
      setError(err.message || 'Failed to download experience letter. This letter is only available for employees who have left the organization.');
    }
  };

  return (
    <div className="space-y-6">
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess('')} />}

      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900">My Letters</h2>
        <p className="text-gray-500">Download your official employment letters</p>
      </div>

      {/* Generate Letters Section */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
            <FileText className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Auto-Generated Letters</h3>
            <p className="text-sm text-gray-500">System-generated letters based on your employment record</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Offer Letter */}
          <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
            </div>
            <h4 className="font-semibold text-blue-900 mb-1">Offer Letter</h4>
            <p className="text-sm text-blue-700 mb-3">
              Formal job offer with compensation details
            </p>
            <Button
              onClick={handleOfferLetter}
              loading={offerLoading}
              size="sm"
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>

          {/* Appointment Letter */}
          <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200">
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center">
                <BadgeCheck className="w-5 h-5 text-white" />
              </div>
            </div>
            <h4 className="font-semibold text-green-900 mb-1">Appointment Letter</h4>
            <p className="text-sm text-green-700 mb-3">
              Terms and conditions of employment
            </p>
            <Button
              onClick={handleAppointmentLetter}
              loading={appointmentLoading}
              size="sm"
              className="w-full bg-green-600 hover:bg-green-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>

          {/* Experience Letter */}
          <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl border border-amber-200">
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
            </div>
            <h4 className="font-semibold text-amber-900 mb-1">Experience Letter</h4>
            <p className="text-sm text-amber-700 mb-3">
              Available after leaving organization
            </p>
            <Button
              onClick={handleExperienceLetter}
              loading={experienceLoading}
              size="sm"
              className="w-full bg-amber-600 hover:bg-amber-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>
        </div>
      </Card>

      {/* Uploaded Letters Section */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <FolderOpen className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Letters from HR</h3>
            <p className="text-sm text-gray-500">Letters uploaded by your organization</p>
          </div>
        </div>

        {loadingLetters ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : uploadedLetters.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No letters from HR yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {uploadedLetters.map((letter) => (
              <div 
                key={letter._id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{letter.title}</h4>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Badge variant="secondary" className="text-xs">
                        {letterTypeLabels[letter.letterType] || letter.letterType}
                      </Badge>
                      <span>•</span>
                      <span>{new Date(letter.issueDate).toLocaleDateString('en-IN')}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => window.open(letter.fileUrl, '_blank')}
                    title="View"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => letterAPI.downloadUploadedLetter(letter._id)}
                    title="Download"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Info Card */}
      <Card className="p-6 bg-gray-50 border-gray-200">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-1">About Your Letters</h4>
            <p className="text-sm text-gray-600">
              These are official documents generated based on your employment records. 
              If you notice any discrepancies in the information, please contact your HR department.
              Letters are generated as PDF files and will be downloaded to your device.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default EmployeeLettersPage;
